import { fmt, iso } from '../utils/formatters';
import type { Token, Prices, FIFOResults } from '../types';

/**
 * Export Service - Export de données en différents formats
 */

/**
 * Export en CSV
 */
export function exportToCSV(data: Record<string, any>[], filename: string = 'export.csv'): void {
  if (!data || !data.length) {
    console.warn('No data to export');
    return;
  }

  // Générer les headers depuis la première ligne
  const headers = Object.keys(data[0]);

  // Créer les lignes CSV
  const csvRows: string[] = [];

  // Header
  csvRows.push(headers.join(','));

  // Data rows
  for (const row of data) {
    const values = headers.map(header => {
      const value = row[header];
      // Escape les guillemets et wrap avec guillemets si contient virgule
      const escaped = String(value).replace(/"/g, '""');
      return /[,"\n]/.test(escaped) ? `"${escaped}"` : escaped;
    });
    csvRows.push(values.join(','));
  }

  // Créer le Blob et télécharger
  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, filename);
}

/**
 * Export positions en CSV
 */
export function exportPositionsCSV(tokens: Token[]): void {
  const positions: Record<string, any>[] = [];

  tokens.forEach(token => {
    if (token.positions && token.positions.length > 0) {
      token.positions.forEach(pos => {
        positions.push({
          Token: token.displayName || token.id,
          Symbol: token.id,
          Type: pos.type === 'buy' ? 'Achat' : 'Vente',
          Date: pos.date,
          Prix: pos.price,
          Quantité: pos.quantity,
          Montant: pos.price * pos.quantity,
          Note: pos.note || ''
        });
      });
    }
  });

  exportToCSV(positions, `positions_${iso(new Date())}.csv`);
}

/**
 * Export portfolio en CSV
 */
export function exportPortfolioCSV(tokens: Token[], prices: Prices, fifoResults: FIFOResults): void {
  const data = tokens.map(token => {
    const fifo = fifoResults[token.id] || {};
    const currentPrice = prices[token.id]?.usd || 0;
    const qty = fifo.remainingQty || 0;
    const avgCost = fifo.avgCost || 0;
    const currentValue = qty * currentPrice;
    const totalCost = qty * avgCost;
    const unrealizedPL = currentValue - totalCost;
    const unrealizedPct = totalCost > 0 ? (unrealizedPL / totalCost) * 100 : 0;

    return {
      Token: token.displayName || token.id,
      Symbol: token.id,
      Quantité: fmt(qty, 8),
      'Prix Moyen': fmt(avgCost, 2),
      'Prix Actuel': fmt(currentPrice, 2),
      'Valeur Actuelle': fmt(currentValue, 2),
      'Coût Total': fmt(totalCost, 2),
      'P/L Non Réalisé': fmt(unrealizedPL, 2),
      'P/L %': fmt(unrealizedPct, 2),
      'P/L Réalisé': fmt(fifo.realizedPL || 0, 2)
    };
  });

  exportToCSV(data, `portfolio_${iso(new Date())}.csv`);
}

/**
 * Export en JSON
 */
export function exportToJSON(data: any, filename: string = 'export.json'): void {
  console.log('📄 [exportToJSON] Starting JSON export...');

  // Step 1: Stringify
  const jsonString = JSON.stringify(data, null, 2);
  console.log('📄 [exportToJSON] JSON stringified, length:', jsonString.length);

  // Step 2: Create blob
  const blob = new Blob([jsonString], { type: 'application/json' });
  console.log('📄 [exportToJSON] Blob created, size:', blob.size);

  // Step 3: Download
  downloadBlob(blob, filename);
  console.log('📄 [exportToJSON] downloadBlob called');
}

/**
 * Export backup complet
 */
export function exportBackup(state: any): void {
  const backup = {
    version: 2,
    exportDate: new Date().toISOString(),
    state: state
  };

  exportToJSON(backup, `wealthhub_backup_${iso(new Date())}.json`);
}

/**
 * Import depuis JSON
 */
export function importFromJSON(file: File, callback: (error: Error | null, data: any) => void): void {
  const reader = new FileReader();

  reader.onload = (event) => {
    try {
      const result = event.target?.result;
      if (typeof result === 'string') {
        const data = JSON.parse(result);
        callback(null, data);
      } else {
        callback(new Error('Invalid file content'), null);
      }
    } catch (error) {
      callback(error as Error, null);
    }
  };

  reader.onerror = () => {
    callback(new Error('File reading error'), null);
  };

  reader.readAsText(file);
}

/**
 * Utilitaire pour télécharger un Blob
 * Uses standard download approach - Web Share API is problematic on desktop browsers
 */
function downloadBlob(blob: Blob, filename: string): void {
  console.log('📥 downloadBlob called:', { filename, blobSize: blob.size, blobType: blob.type });

  // Detect if we're on a mobile device (iOS/Android)
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // Only try Web Share API on mobile devices where it works reliably for files
  if (isMobile && navigator.share && navigator.canShare) {
    try {
      const file = new File([blob], filename, { type: blob.type });
      if (navigator.canShare({ files: [file] })) {
        console.log('📤 Using Web Share API for mobile...');
        navigator.share({
          files: [file],
          title: 'Portfolio Export',
        }).then(() => {
          console.log('✅ Shared successfully');
        }).catch((error) => {
          console.warn('Share failed, falling back to download:', error);
          fallbackDownload(blob, filename);
        });
        return;
      }
    } catch (e) {
      console.warn('Web Share API check failed:', e);
      // Fall through to standard download
    }
  }

  // Use standard download for desktop and as fallback
  console.log('📥 Using standard download method...');
  fallbackDownload(blob, filename);
}

/**
 * Fallback download method using anchor element with download attribute
 */
function fallbackDownload(blob: Blob, filename: string): void {
  try {
    console.log('📥 [fallbackDownload] Creating object URL...');
    const url = URL.createObjectURL(blob);
    console.log('📥 [fallbackDownload] Object URL created:', url.substring(0, 50) + '...');

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    console.log('📥 [fallbackDownload] Appending link to body...');
    document.body.appendChild(link);

    // Use setTimeout to ensure the click happens after DOM update
    setTimeout(() => {
      console.log('📥 [fallbackDownload] Triggering click...');
      link.click();
      console.log('📥 [fallbackDownload] Click triggered, cleaning up...');

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log('✅ [fallbackDownload] Cleanup complete');
      }, 100);
    }, 0);
  } catch (err) {
    console.error('❌ [fallbackDownload] Error:', err);
    // Last resort: try window.open with data URL
    try {
      console.log('📥 [fallbackDownload] Trying data URL fallback...');
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        window.open(dataUrl, '_blank');
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      console.error('❌ [fallbackDownload] Data URL fallback also failed:', e);
    }
  }
}

/**
 * Copy to clipboard
 */
export function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  } else {
    // Fallback pour navigateurs plus anciens
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return Promise.resolve();
  }
}
